/**
 * WeeklyPlanChat — Pianificazione settimana con il Coach.
 *
 * Complementare a WeeklyReviewChat (che consuntiva la settimana): questo aiuta
 * a PROGETTARE la settimana prima che inizi. Usa il momento `settimana_plan`.
 *
 * Auto-detect della settimana bersaglio:
 *   - Lun-Gio (weekday 1-4): pianifica la settimana IN CORSO (sei ancora in tempo)
 *   - Ven-Dom (weekday 5, 6, 0): pianifica la settimana SUCCESSIVA
 *
 * L'utente può comunque switchare avanti/indietro col picker (per pianificare
 * una settimana futura specifica o rivedere una passata).
 */
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Send, Loader2, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { fetchStateSettimana } from '../lib/api';
import { useCoachChat } from '../lib/useCoachChat';
import CoachStatePanel from './nav/CoachStatePanel';
import CoachStatePanelSkeleton from './nav/CoachStatePanelSkeleton';
import EndSessionButton from './nav/EndSessionButton';
import ChatSkeleton from './nav/ChatSkeleton';
import MemoryChip from './nav/MemoryChip';
import CoachMessageBubble from './nav/CoachMessageBubble';
import PolarStarBanner from './nav/PolarStarBanner';
import { Textarea } from './ui/textarea';

// ── Utility ISO week (shared logic con WeeklyReviewChat) ────────────────
function shiftWeek(label, delta) {
  const m = label.match(/^(\d+)-W(\d+)$/);
  if (!m) return label;
  const y = parseInt(m[1]), w = parseInt(m[2]);
  const jan4 = new Date(y, 0, 4);
  const jan4DayOfWeek = jan4.getDay() || 7;
  const mondayW1 = new Date(jan4); mondayW1.setDate(jan4.getDate() - jan4DayOfWeek + 1);
  const monday = new Date(mondayW1); monday.setDate(mondayW1.getDate() + (w - 1) * 7 + delta * 7);
  const tmp = new Date(monday); tmp.setHours(0, 0, 0, 0);
  tmp.setDate(tmp.getDate() + 4 - (tmp.getDay() || 7));
  const yearStart = new Date(tmp.getFullYear(), 0, 1);
  const weekNum = Math.ceil((((tmp - yearStart) / 86400000) + 1) / 7);
  return `${tmp.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
}

function currentIsoWeek() {
  const d = new Date(); d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  const weekNum = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return `${d.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
}

/**
 * Auto-detect: se siamo Ven-Dom pianifichiamo LA SETTIMANA SUCCESSIVA.
 * Altrimenti pianifichiamo la settimana in corso (Lun-Gio siamo ancora in
 * tempo per aggiustare MIT e Azioni).
 */
function autodetectPlanWeek() {
  const dow = new Date().getDay(); // 0=Dom, 1=Lun, ... 6=Sab
  const isLateWeek = dow === 0 || dow >= 5;
  const cur = currentIsoWeek();
  return isLateWeek ? shiftWeek(cur, +1) : cur;
}

export default function WeeklyPlanChat() {
  const [settimana, setSettimana] = useState(autodetectPlanWeek());
  const currentWeek = useMemo(() => currentIsoWeek(), []);
  const isNextWeek = settimana !== currentWeek;

  const {
    stato, aree, messages, input, setInput, sending,
    applying, pendingOps, editingOpsJson, setEditingOpsJson,
    highlightIds, scrollRef, inputRef,
    handleSend, handleKeyDown, handleConfirmOps, handleCancelOps,
    handleCloseItem, handleUpdateItem,
    endSession,
  } = useCoachChat({
    momento: 'settimana_plan',
    fetchStateFn: fetchStateSettimana,
    param: settimana,
    resumeGreetingText: (s) => `Riprendiamo la pianificazione della ${s.settimana}.`,
  });

  if (!stato) {
    return (
      <div className="min-h-screen bg-canvas text-ink flex flex-col">
        <div className="flex-1 max-w-[1600px] w-full mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[1fr_480px] gap-8">
          <ChatSkeleton />
          <CoachStatePanelSkeleton title="Stato della settimana" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas text-ink flex flex-col">
      <div className="border-b border-line shrink-0">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-soft hover:text-ink transition-colors" data-testid="back-to-dashboard">
              <ArrowLeft size={14} strokeWidth={1.5} /> Dashboard
            </Link>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-mute">/</span>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-ink flex items-center gap-2">
              <Calendar size={12} strokeWidth={1.5} />
              Pianifica settimana
            </span>
            {isNextWeek && (
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent border border-accent px-2 py-0.5" data-testid="future-week-badge">
                settimana prossima
              </span>
            )}
          </div>
          <div className="flex items-center gap-2" data-testid="week-switcher">
            <button
              onClick={() => setSettimana((s) => shiftWeek(s, -1))}
              className="h-8 w-8 flex items-center justify-center text-soft hover:bg-ink hover:text-canvas transition-colors"
              data-testid="week-prev" aria-label="Settimana precedente"
            >
              <ChevronLeft size={14} strokeWidth={1.5} />
            </button>
            <span className="font-display text-xl tracking-tight px-3" data-testid="week-label">{stato.settimana}</span>
            <button
              onClick={() => setSettimana((s) => shiftWeek(s, +1))}
              className="h-8 w-8 flex items-center justify-center text-soft hover:bg-ink hover:text-canvas transition-colors"
              data-testid="week-next" aria-label="Settimana successiva"
            >
              <ChevronRight size={14} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 max-w-[1600px] w-full mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[1fr_500px] gap-8">
        {/* LEFT — Chat */}
        <div className="flex flex-col min-h-0 border border-line bg-paper">
          <div className="flex items-baseline justify-between px-6 py-4 border-b border-line">
            <h2 className="font-display text-lg tracking-tight">QuantumCoach · Pianificazione</h2>
            <div className="flex items-center gap-3">
              <EndSessionButton onEnd={endSession} testid="settimana-plan-end-session-btn" />
              <span className="mono-label">{stato.lun} → {stato.dom}</span>
            </div>
          </div>

          {/* INPUT in alto (sticky) */}
          <div className="border-b border-line p-4 sticky top-0 bg-paper z-10">
            <div className="relative">
              <Textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Priorità della settimana, vincoli reali (ore, appuntamenti), cosa porti avanti dalla scorsa settimana. Rispondi una domanda alla volta."
                className="rounded-none border-line focus-visible:ring-0 focus-visible:border-ink resize-none font-body text-base pr-14 min-h-[88px]"
                disabled={sending}
                data-testid="chat-input"
              />
              <button
                onClick={handleSend}
                disabled={sending || !input.trim()}
                className="absolute bottom-3 right-3 h-9 w-9 bg-ink text-canvas flex items-center justify-center hover:bg-accent disabled:bg-mute transition-colors"
                data-testid="send-message-btn"
                aria-label="Invia"
              >
                <Send size={14} strokeWidth={1.8} />
              </button>
            </div>
            {sending && (
              <div className="flex items-center gap-2 text-mute font-mono text-xs mt-3">
                <Loader2 size={14} className="animate-spin" strokeWidth={1.5} />
                {messages.length === 0
                  ? 'QuantumCoach sta preparando le priorità della settimana…'
                  : 'QuantumCoach sta pensando…'}
              </div>
            )}
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-6" data-testid="chat-messages">
            <PolarStarBanner stato={stato} />
            <MemoryChip />
            {[...messages].reverse().map((m, revIdx) => {
              if (m.hidden) return null;
              const idx = messages.length - 1 - revIdx;
              const isLast = idx === messages.length - 1;
              return (
                <CoachMessageBubble
                  key={idx}
                  message={m}
                  isPending={pendingOps && pendingOps.msgIdx === idx}
                  editingJson={pendingOps && pendingOps.msgIdx === idx ? editingOpsJson : null}
                  setEditingJson={setEditingOpsJson}
                  applying={applying}
                  onConfirm={handleConfirmOps}
                  onCancel={handleCancelOps}
                  onEdit={() => setEditingOpsJson(JSON.stringify(pendingOps.operazioni, null, 2))}
                  onChipClick={isLast && !sending ? handleSend : null}
                  aree={aree}
                />
              );
            })}
          </div>
        </div>

        {/* RIGHT — Stato Settimana */}
        <CoachStatePanel
          title="Stato della settimana"
          subtitle={`${stato.lun} → ${stato.dom}`}
          obiettivi={stato.obiettivi || []}
          keyResults={stato.key_results || []}
          azioni={stato.azioni || []}
          mit={stato.mit || []}
          storageKey="weekly-plan"
          testid="week-plan-panel"
          highlightIds={highlightIds}
          onCloseItem={handleCloseItem}
          onUpdateItem={handleUpdateItem}
        />
      </div>
    </div>
  );
}
